import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Canvas as FabricCanvas, Textbox, Rect, Line, FabricText, FabricImage, Group } from 'fabric';
import { AdminLayout } from '../layout/AdminLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { templateApi, fontApi } from '../../lib/api';
import { toast } from 'sonner';
import {
    Save, ArrowLeft, Type, Image as ImageIcon, Square, Minus, QrCode,
    Loader2, Trash2, Copy, Bold, Italic, Underline, AlignLeft, AlignCenter,
    AlignRight, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown, Undo2, Redo2,
    ZoomIn, ZoomOut, FileText, Plus, X, Eye, Download, CaseUpper, History, RotateCcw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");
const SCALE = 2.5; // px per mm

// Rewrite asset URLs from old domains to current backend
const rewriteAssetUrls = (pageData) => {
    if (!pageData?.objects) return pageData;
    const apiBase = BACKEND_URL + '/api/';
    pageData.objects.forEach(obj => {
        if (obj.src && obj.src.includes('/api/template-assets/') && !obj.src.startsWith(BACKEND_URL)) {
            const assetPath = obj.src.substring(obj.src.indexOf('/api/template-assets/'));
            obj.src = BACKEND_URL + assetPath;
        }
    });
    return pageData;
};

const FORMATS = {
    'a4-portrait':  { label: 'DIN A4 Hochformat', w: 210, h: 297 },
    'a4-landscape': { label: 'DIN A4 Querformat',  w: 297, h: 210 },
};

const PLACEHOLDERS = [
    { key: '{{vorname}}', label: 'Vorname' },
    { key: '{{nachname}}', label: 'Nachname' },
    { key: '{{vorname_nachname}}', label: 'Vorname Nachname' },
    { key: '{{titel_vorname_nachname}}', label: 'Titel Vorname Nachname' },
    { key: '{{titel}}', label: 'Titel' },
    { key: '{{strasse}}', label: 'Straße' },
    { key: '{{plz}}', label: 'PLZ' },
    { key: '{{ort}}', label: 'Ort' },
    { key: '{{plz_ort}}', label: 'PLZ Ort' },
    { key: '{{geburtsdatum}}', label: 'Geburtsdatum' },
    { key: '{{lizenztyp}}', label: 'Lizenztyp' },
    { key: '{{lizenznummer}}', label: 'Lizenznummer' },
    { key: '{{ausstellungsdatum}}', label: 'Ausstellungsdatum' },
    { key: '{{erstausstellungsdatum}}', label: 'Erstausstellungsdatum' },
    { key: '{{gueltig_bis}}', label: 'Gültig bis' },
    { key: '{{qualifikation}}', label: 'Qualifikation' },
    { key: '{{seminar_beschreibung}}', label: 'Seminar-Beschreibung (rich)' },
    { key: '{{zertifikatsinhalt}}', label: 'Zertifikatsinhalt (rich)' },
    // Zahlungsbestätigung (payment_receipt)
    { key: '{{betrag}}', label: 'Betrag' },
    { key: '{{zahlungsdatum}}', label: 'Zahlungsdatum' },
    { key: '{{zahlungsreferenz}}', label: 'Zahlungsreferenz' },
    { key: '{{rechnungsnummer}}', label: 'Belegnummer' },
    { key: '{{seminar_titel}}', label: 'Seminartitel' },
    { key: '{{kursnummer}}', label: 'Kursnummer' },
    { key: '{{kursdatum}}', label: 'Kursdatum' },
    { key: '{{kursort}}', label: 'Kursort' },
    { key: '{{absender_name}}', label: 'Absender Name' },
    { key: '{{absender_adresse}}', label: 'Absender Adresse' },
    { key: '{{absender_ort}}', label: 'Absender Ort' },
    { key: '{{absender_telefon}}', label: 'Absender Telefon' },
    { key: '{{absender_email}}', label: 'Absender E-Mail' },
    { key: '{{absender_web}}', label: 'Absender Web' },
    { key: '{{bank_kontoinhaber}}', label: 'Bank Kontoinhaber' },
    { key: '{{bank_name}}', label: 'Bank Name' },
    { key: '{{bank_iban}}', label: 'Bank IBAN' },
    { key: '{{bank_bic}}', label: 'Bank BIC' },
    { key: '{{bank_adresse}}', label: 'Bank Adresse' },
    // In-House-Rechnung (in_house_invoice)
    { key: '{{firma_name}}', label: 'Firma Name' },
    { key: '{{firma_strasse}}', label: 'Firma Straße' },
    { key: '{{firma_plz_ort}}', label: 'Firma PLZ Ort' },
    { key: '{{firma_ustid_zeile}}', label: 'Firma USt-IdNr. (Zeile)' },
    { key: '{{rechnungsdatum}}', label: 'Rechnungsdatum' },
    { key: '{{faellig_bis}}', label: 'Fällig bis' },
    { key: '{{kursinfo}}', label: 'Kursnummer · Kursdatum' },
    { key: '{{positionstext}}', label: 'Positionstext (Pauschal / TN × Preis)' },
    { key: '{{teilnehmerzahl}}', label: 'Teilnehmerzahl' },
    { key: '{{steuerhinweis}}', label: 'Steuerhinweis' },
    { key: '{{zahlungshinweis}}', label: 'Zahlungshinweis / Bezahlt-Vermerk (rich)' },
];

const CUSTOM_PROPS = ['id', 'placeholderKey', 'isPlaceholder', 'isBackground', 'textTransform', 'isGuide', 'guideOrientation'];

export default function TemplateEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const canvasElRef = useRef(null);
    const fcRef = useRef(null);
    const fileInputRef = useRef(null);
    const bgInputRef = useRef(null);

    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [selObj, setSelObj] = useState(null);
    const [props, setProps] = useState({});
    const [zoom, setZoom] = useState(100);
    const [ready, setReady] = useState(false);
    const [bgVersion, setBgVersion] = useState(0); // trigger re-render on bg change
    const [editorFonts, setEditorFonts] = useState(['Helvetica','Arial','Times New Roman','Georgia','Courier New']);

    // Versions-Historie (letzte ~10 Stände, neuestes zuerst)
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [versions, setVersions] = useState([]);
    const [versionsLoading, setVersionsLoading] = useState(false);

    const openVersions = async () => {
        setVersionsOpen(true);
        setVersionsLoading(true);
        try {
            const res = await templateApi.listVersions(template.id);
            setVersions(res.data || []);
        } catch {
            toast.error('Versionsverlauf konnte nicht geladen werden');
        } finally { setVersionsLoading(false); }
    };

    const restoreVersion = async (versionId, objectCount) => {
        const msg = objectCount === 0
            ? 'Diese Version enthält keine Elemente. Soll trotzdem wiederhergestellt werden?'
            : `Diese Version mit ${objectCount} Element${objectCount === 1 ? '' : 'en'} wiederherstellen?\n\n` +
              'Der aktuelle Stand wird zur Sicherheit als neue Version abgelegt — die Wiederherstellung lässt sich also rückgängig machen.';
        if (!window.confirm(msg)) return;
        try {
            await templateApi.restoreVersion(template.id, versionId);
            toast.success('Vorlage wiederhergestellt — Seite wird neu geladen');
            setVersionsOpen(false);
            // Seite neu laden, damit der Editor frisch initialisiert
            setTimeout(() => window.location.reload(), 800);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Wiederherstellung fehlgeschlagen');
        }
    };

    // Multi-page state
    const [pages, setPages] = useState([null]); // array of canvas JSON (null = empty page)
    const [currentPage, setCurrentPage] = useState(0);
    const [format, setFormat] = useState('a4-portrait');

    const fmt = FORMATS[format];
    const canvasW = Math.round(fmt.w * SCALE);
    const canvasH = Math.round(fmt.h * SCALE);

    // === Save current page canvas to pages array ===
    const saveCurrentPageToState = useCallback(() => {
        if (!fcRef.current) return;
        const json = fcRef.current.toJSON(CUSTOM_PROPS);
        const objects = fcRef.current.getObjects();
        json.objects?.forEach((objJson, idx) => {
            const liveObj = objects[idx];
            if (!liveObj) return;
            if (liveObj.isBackground) objJson.isBackground = true;
            if (liveObj.textTransform) objJson.textTransform = liveObj.textTransform;
            if (liveObj.isGuide) {
                objJson.isGuide = true;
                objJson.guideOrientation = liveObj.guideOrientation;
            }
            if (liveObj.isPlaceholder) objJson.isPlaceholder = true;
            if (liveObj.placeholderKey) objJson.placeholderKey = liveObj.placeholderKey;
        });
        setPages(prev => {
            const copy = [...prev];
            copy[currentPage] = json;
            return copy;
        });
    }, [currentPage]);

    // === Init / Reinit Fabric Canvas ===
    useEffect(() => {
        if (loading || !canvasElRef.current) return;

        // Dispose existing canvas
        if (fcRef.current) {
            fcRef.current.dispose();
            fcRef.current = null;
            setReady(false);
        }

        const fc = new FabricCanvas(canvasElRef.current, {
            width: canvasW,
            height: canvasH,
            backgroundColor: '#FFFFFF',
            selection: true,
            preserveObjectStacking: true,
        });
        fcRef.current = fc;

        const onSelect = (e) => { const o = e.selected?.[0]; if (o) { setSelObj(o); setProps(readProps(o)); } };
        fc.on('selection:created', onSelect);
        fc.on('selection:updated', onSelect);
        fc.on('selection:cleared', () => { setSelObj(null); setProps({}); });
        fc.on('object:modified', (e) => {
            const o = e.target || fc.getActiveObject();
            if (o && (o.type === 'textbox' || o.type === 'i-text' || o.type === 'text')) {
                const sx = o.scaleX || 1;
                const sy = o.scaleY || 1;
                if (sx !== 1 || sy !== 1) {
                    o.set({
                        width: o.width * sx,
                        fontSize: Math.round(o.fontSize * sy),
                        scaleX: 1,
                        scaleY: 1,
                    });
                    o.setCoords();
                    fc.renderAll();
                }
            }
            if (o) { setSelObj(o); setProps(readProps(o)); }
        });

        // Snap-to-Guide: InDesign-style snapping
        const SNAP_DIST = 8; // px threshold
        fc.on('object:moving', (e) => {
            const obj = e.target;
            if (!obj || obj.isGuide || obj.isBackground) return;
            const guides = fc.getObjects().filter(g => g.isGuide);
            if (!guides.length) return;

            // Calculate object edges from obj.left/top (same coord space as guides)
            const w = obj.getScaledWidth();
            const h = obj.getScaledHeight();
            const oX = obj.originX || 'center';
            const oY = obj.originY || 'center';

            let edgeL, edgeR, edgeCX;
            if (oX === 'left')        { edgeL = obj.left;           edgeCX = obj.left + w/2;     edgeR = obj.left + w; }
            else if (oX === 'right')  { edgeL = obj.left - w;       edgeCX = obj.left - w/2;     edgeR = obj.left; }
            else /* center */         { edgeL = obj.left - w/2;     edgeCX = obj.left;            edgeR = obj.left + w/2; }

            let edgeT, edgeB, edgeCY;
            if (oY === 'top')         { edgeT = obj.top;            edgeCY = obj.top + h/2;      edgeB = obj.top + h; }
            else if (oY === 'bottom') { edgeT = obj.top - h;        edgeCY = obj.top - h/2;      edgeB = obj.top; }
            else /* center */         { edgeT = obj.top - h/2;      edgeCY = obj.top;             edgeB = obj.top + h/2; }

            // Find the single closest X snap and Y snap
            let bestDx = null, bestXDist = SNAP_DIST;
            let bestDy = null, bestYDist = SNAP_DIST;

            for (const g of guides) {
                if (g.guideOrientation === 'vertical') {
                    const gx = g.left;
                    for (const edge of [edgeL, edgeCX, edgeR]) {
                        const d = Math.abs(edge - gx);
                        if (d < bestXDist) { bestXDist = d; bestDx = gx - edge; }
                    }
                } else {
                    const gy = g.top;
                    for (const edge of [edgeT, edgeCY, edgeB]) {
                        const d = Math.abs(edge - gy);
                        if (d < bestYDist) { bestYDist = d; bestDy = gy - edge; }
                    }
                }
            }

            if (bestDx !== null) obj.set('left', obj.left + bestDx);
            if (bestDy !== null) obj.set('top', obj.top + bestDy);
        });

        // Load page data
        const pageData = pages[currentPage];
        if (pageData && pageData.objects && pageData.objects.length > 0) {
            rewriteAssetUrls(pageData);
            fc.loadFromJSON(pageData).then(() => {
                restoreBackgroundFlags(fc, pageData);
                lockBackgroundObjects(fc);
                // Normalize text objects: absorb scaleX/scaleY into width/fontSize
                fc.getObjects().forEach(o => {
                    if ((o.type === 'textbox' || o.type === 'i-text' || o.type === 'text') && ((o.scaleX || 1) !== 1 || (o.scaleY || 1) !== 1)) {
                        o.set({
                            width: o.width * (o.scaleX || 1),
                            fontSize: Math.round(o.fontSize * (o.scaleY || 1)),
                            scaleX: 1,
                            scaleY: 1,
                        });
                        o.setCoords();
                    }
                });
                fc.renderAll();
                setReady(true);
                setBgVersion(v => v + 1);
            });
        } else {
            setReady(true);
        }

        return () => { fc.dispose(); fcRef.current = null; setReady(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, currentPage, canvasW, canvasH]);

    // === Load Template ===
    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const res = await templateApi.getOne(id);
                const tmpl = res.data;
                setTemplate(tmpl);

                // Parse content
                let content = {};
                try { content = typeof tmpl.content === 'string' ? JSON.parse(tmpl.content) : tmpl.content; } catch {}

                // Determine format
                const orient = tmpl.orientation || 'portrait';
                setFormat(orient === 'landscape' ? 'a4-landscape' : 'a4-portrait');

                // Multi-page: content.pages array or single canvas
                if (content.pages && Array.isArray(content.pages)) {
                    setPages(content.pages.length > 0 ? content.pages : [null]);
                } else if (content.objects) {
                    setPages([content]);
                } else {
                    setPages([null]);
                }
                setCurrentPage(0);
            } catch {
                toast.error('Vorlage nicht gefunden');
                navigate('/admin/settings/templates');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate]);

    // === Load available fonts from API ===
    useEffect(() => {
        (async () => {
            try {
                const res = await fontApi.getAll();
                if (res.data?.editor_fonts?.length) {
                    setEditorFonts(res.data.editor_fonts);
                }
            } catch {
                // Keep default fonts on error
            }
        })();
    }, []);

    // === Helpers ===
    const lockBackgroundObjects = (fc) => {
        fc.getObjects().forEach(o => {
            if (o.isBackground) {
                o.set({ selectable: false, evented: false, hasControls: false, hasBorders: false, lockMovementX: true, lockMovementY: true });
            }
        });
    };

    const getBackgroundObj = () => {
        if (!fcRef.current) return null;
        return fcRef.current.getObjects().find(o => o.isBackground);
    };

    // After loading canvas from JSON, restore custom flags from the page data
    const restoreBackgroundFlags = (fc, pageData) => {
        if (!pageData?.objects) return;
        const fabricObjs = fc.getObjects();
        pageData.objects.forEach((objJson, idx) => {
            if (!fabricObjs[idx]) return;
            if (objJson.isBackground) fabricObjs[idx].isBackground = true;
            if (objJson.textTransform) fabricObjs[idx].textTransform = objJson.textTransform;
            if (objJson.isPlaceholder) fabricObjs[idx].isPlaceholder = true;
            if (objJson.placeholderKey) fabricObjs[idx].placeholderKey = objJson.placeholderKey;
            if (objJson.isGuide) {
                fabricObjs[idx].isGuide = true;
                fabricObjs[idx].guideOrientation = objJson.guideOrientation;
                fabricObjs[idx].set({
                    hasControls: false, hasBorders: false,
                    lockMovementX: objJson.guideOrientation === 'horizontal',
                    lockMovementY: objJson.guideOrientation === 'vertical',
                    strokeDashArray: [5, 5],
                    excludeFromExport: false,
                });
            }
        });
    };

    // Unified helper: origin offset for converting between edge and origin coordinates
    const _edgeOffset = (o) => {
        const w = (o.width || 0) * (o.scaleX || 1);
        const h = (o.height || 0) * (o.scaleY || 1);
        const ox = o.originX || 'center';
        const oy = o.originY || 'center';
        return {
            dx: ox === 'center' ? w/2 : ox === 'right' ? w : 0,
            dy: oy === 'center' ? h/2 : oy === 'bottom' ? h : 0,
            w, h
        };
    };

    const readProps = (o) => {
        if (!o) return {};
        const { dx, dy, w, h } = _edgeOffset(o);
        return {
            left: Math.round((o.left || 0) - dx), top: Math.round((o.top || 0) - dy),
            width: Math.round(w), height: Math.round(h),
            fill: o.fill || '#000000', fontSize: o.fontSize || 16,
            fontFamily: o.fontFamily || 'Helvetica',
            fontWeight: o.fontWeight || 'normal', fontStyle: o.fontStyle || 'normal',
            underline: o.underline || false, textAlign: o.textAlign || 'left',
            lineHeight: Math.round((o.lineHeight || 1.16) * 100) / 100,
            textTransform: o.textTransform || 'none',
            opacity: Math.round((o.opacity || 1) * 100), angle: Math.round(o.angle || 0),
            stroke: o.stroke || '', strokeWidth: o.strokeWidth || 0,
            type: o.type, text: o.text || '',
            isPlaceholder: o.isPlaceholder || false, placeholderKey: o.placeholderKey || '',
            isGuide: o.isGuide || false, guideOrientation: o.guideOrientation || '',
            guidePosMMX: o.guideOrientation === 'vertical' ? Math.round((o.left || 0) / SCALE * 10) / 10 : null,
            guidePosMMY: o.guideOrientation === 'horizontal' ? Math.round((o.top || 0) / SCALE * 10) / 10 : null,
        };
    };

    const setProp = (key, val) => {
        const o = fcRef.current?.getActiveObject();
        if (!o) return;
        const isText = o.type === 'textbox' || o.type === 'i-text' || o.type === 'text';
        if (key === 'left') {
            const { dx } = _edgeOffset(o);
            o.set('left', val + dx);
        }
        else if (key === 'top') {
            const { dy } = _edgeOffset(o);
            o.set('top', val + dy);
        }
        else if (key === 'width') {
            if (isText) { o.set('width', val); o.set('scaleX', 1); }
            else o.set('scaleX', val / o.width);
        }
        else if (key === 'height') {
            if (isText) { /* height is determined by text content, skip */ }
            else o.set('scaleY', val / o.height);
        }
        else if (key === 'lineHeight') o.set('lineHeight', parseFloat(val) || 1.16);
        else if (key === 'opacity') o.set('opacity', val / 100);
        else if (key === 'fontSize') o.set('fontSize', parseInt(val));
        else o.set(key, val);
        o.setCoords();
        fcRef.current.renderAll();
        setProps(readProps(o));
    };

    // === Page management ===
    const switchPage = (idx) => {
        if (idx === currentPage || !fcRef.current) return;
        saveCurrentPageToState();
        setSelObj(null);
        setProps({});
        setCurrentPage(idx);
    };

    const addPage = () => {
        saveCurrentPageToState();
        setPages(prev => [...prev, null]);
        setCurrentPage(pages.length); // switch to new page
    };

    const deletePage = (idx) => {
        if (pages.length <= 1) { toast.error('Mindestens eine Seite erforderlich'); return; }
        setPages(prev => prev.filter((_, i) => i !== idx));
        if (currentPage >= idx && currentPage > 0) setCurrentPage(currentPage - 1);
        else if (currentPage === idx) setCurrentPage(Math.max(0, idx - 1));
    };

    // === Format change ===
    const changeFormat = (newFormat) => {
        saveCurrentPageToState();
        setFormat(newFormat);
        // Canvas will be reinitialised by useEffect since canvasW/canvasH change
    };

    // === Tool functions ===
    const addText = (text = 'Neuer Text') => {
        if (!fcRef.current) return;
        const tb = new Textbox(text, { left: 50, top: 50, width: 200, fontSize: 18, fontFamily: 'Helvetica', fill: '#000000' });
        fcRef.current.add(tb);
        fcRef.current.setActiveObject(tb);
        fcRef.current.renderAll();
    };
    const addPlaceholder = (ph) => {
        if (!fcRef.current) return;
        const tb = new Textbox(ph.key, { left: 50, top: 50, width: 250, fontSize: 18, fontFamily: 'Helvetica', fill: '#333333', id: `ph-${Date.now()}`, isPlaceholder: true, placeholderKey: ph.key });
        fcRef.current.add(tb);
        fcRef.current.setActiveObject(tb);
        fcRef.current.renderAll();
    };
    const addRectangle = () => {
        if (!fcRef.current) return;
        const r = new Rect({ left: 50, top: 50, width: 150, height: 80, fill: 'transparent', stroke: '#000000', strokeWidth: 1 });
        fcRef.current.add(r);
        fcRef.current.setActiveObject(r);
        fcRef.current.renderAll();
    };
    const addHLine = () => {
        if (!fcRef.current) return;
        const l = new Line([50, 100, 400, 100], { stroke: '#000000', strokeWidth: 1 });
        fcRef.current.add(l);
        fcRef.current.setActiveObject(l);
        fcRef.current.renderAll();
    };
    const addQrPlaceholder = () => {
        if (!fcRef.current) return;
        const rect = new Rect({ width: 100, height: 100, fill: '#f0f0f0', stroke: '#999', strokeWidth: 1 });
        const txt = new FabricText('QR', { fontSize: 24, fill: '#666', fontFamily: 'Helvetica', originX: 'center', originY: 'center', left: 50, top: 50 });
        const grp = new Group([rect, txt], { left: 350, top: 600, id: 'qr-placeholder', isPlaceholder: true, placeholderKey: '{{qr_code}}' });
        fcRef.current.add(grp);
        fcRef.current.setActiveObject(grp);
        fcRef.current.renderAll();
    };
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !fcRef.current) return;
        try {
            const res = await templateApi.uploadAsset(file);
            const url = `${BACKEND_URL}${res.data.url}`;
            const img = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
            const scale = Math.min((canvasW * 0.5) / img.width, (canvasH * 0.3) / img.height, 1);
            img.set({ left: 50, top: 50, scaleX: scale, scaleY: scale });
            fcRef.current.add(img);
            fcRef.current.setActiveObject(img);
            fcRef.current.renderAll();
            toast.success('Bild eingefügt');
        } catch { toast.error('Fehler beim Hochladen'); }
        e.target.value = '';
    };

    const handleBackgroundUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !fcRef.current) return;
        try {
            const res = await templateApi.uploadAsset(file);
            const url = `${BACKEND_URL}${res.data.url}`;
            const img = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
            // Scale to fill the entire canvas
            const scaleX = canvasW / img.width;
            const scaleY = canvasH / img.height;
            img.set({
                left: canvasW / 2, top: canvasH / 2,
                scaleX, scaleY,
                originX: 'center', originY: 'center',
                selectable: false, evented: false,
                hasControls: false, hasBorders: false,
                lockMovementX: true, lockMovementY: true,
            });
            img.isBackground = true;
            // Remove existing background first
            const existing = fcRef.current.getObjects().find(o => o.isBackground);
            if (existing) fcRef.current.remove(existing);
            fcRef.current.add(img);
            fcRef.current.sendObjectToBack(img);
            fcRef.current.renderAll();
            setBgVersion(v => v + 1);
            toast.success('Hintergrund gesetzt');
        } catch { toast.error('Fehler beim Hochladen'); }
        e.target.value = '';
    };

    const removeBackground = () => {
        if (!fcRef.current) return;
        const bg = fcRef.current.getObjects().find(o => o.isBackground);
        if (bg) {
            fcRef.current.remove(bg);
            fcRef.current.renderAll();
            setBgVersion(v => v + 1);
            toast.success('Hintergrund entfernt');
        }
    };

    // === Guide functions ===
    const addHGuide = (posY) => {
        if (!fcRef.current) return;
        const y = posY ?? canvasH / 2;
        const guide = new Line([0, 0, canvasW, 0], {
            left: 0, top: y,
            stroke: '#00bcd4', strokeWidth: 1, strokeDashArray: [5, 5],
            selectable: true, evented: true,
            hasControls: false, hasBorders: false,
            lockMovementX: true,
            originX: 'left', originY: 'top',
        });
        guide.isGuide = true;
        guide.guideOrientation = 'horizontal';
        fcRef.current.add(guide);
        fcRef.current.bringObjectToFront(guide);
        fcRef.current.setActiveObject(guide);
        fcRef.current.renderAll();
    };
    const addVGuide = (posX) => {
        if (!fcRef.current) return;
        const x = posX ?? canvasW / 2;
        const guide = new Line([0, 0, 0, canvasH], {
            left: x, top: 0,
            stroke: '#00bcd4', strokeWidth: 1, strokeDashArray: [5, 5],
            selectable: true, evented: true,
            hasControls: false, hasBorders: false,
            lockMovementY: true,
            originX: 'left', originY: 'top',
        });
        guide.isGuide = true;
        guide.guideOrientation = 'vertical';
        fcRef.current.add(guide);
        fcRef.current.bringObjectToFront(guide);
        fcRef.current.setActiveObject(guide);
        fcRef.current.renderAll();
    };
    const setGuidePosMM = (val) => {
        const o = fcRef.current?.getActiveObject();
        if (!o?.isGuide) return;
        const px = val * SCALE;
        if (o.guideOrientation === 'horizontal') o.set('top', px);
        else o.set('left', px);
        o.setCoords();
        fcRef.current.renderAll();
        setProps(readProps(o));
    };
    const getGuideCount = () => {
        if (!fcRef.current) return 0;
        return fcRef.current.getObjects().filter(o => o.isGuide).length;
    };
    const clearAllGuides = () => {
        if (!fcRef.current) return;
        const guides = fcRef.current.getObjects().filter(o => o.isGuide);
        guides.forEach(g => fcRef.current.remove(g));
        fcRef.current.discardActiveObject();
        fcRef.current.renderAll();
        setSelObj(null);
        setProps({});
    };

    const deleteObj = () => { const o = fcRef.current?.getActiveObject(); if (o) { fcRef.current.remove(o); fcRef.current.discardActiveObject(); fcRef.current.renderAll(); setSelObj(null); } };
    const duplicateObj = () => { const o = fcRef.current?.getActiveObject(); if (!o) return; o.clone().then(c => { c.set({ left: (o.left||0)+20, top: (o.top||0)+20 }); fcRef.current.add(c); fcRef.current.setActiveObject(c); fcRef.current.renderAll(); }); };
    const bringFwd = () => { const o = fcRef.current?.getActiveObject(); if (o) { fcRef.current.bringObjectForward(o); fcRef.current.renderAll(); } };
    const sendBwd = () => {
        const o = fcRef.current?.getActiveObject();
        if (!o || !fcRef.current) return;
        fcRef.current.sendObjectBackwards(o);
        // Ensure background stays at bottom
        const bg = fcRef.current.getObjects().find(obj => obj.isBackground);
        if (bg) { fcRef.current.sendObjectToBack(bg); }
        fcRef.current.renderAll();
    };
    const toFront = () => { const o = fcRef.current?.getActiveObject(); if (o) { fcRef.current.bringObjectToFront(o); fcRef.current.renderAll(); } };
    const toBack = () => {
        const o = fcRef.current?.getActiveObject();
        if (!o || !fcRef.current) return;
        fcRef.current.sendObjectToBack(o);
        // Ensure background stays at bottom
        const bg = fcRef.current.getObjects().find(obj => obj.isBackground && obj !== o);
        if (bg) { fcRef.current.sendObjectToBack(bg); }
        fcRef.current.renderAll();
    };

    // === Save ===
    const handleSave = async () => {
        if (!fcRef.current || !template) return;
        setSaving(true);
        try {
            // Save current page first
            const currentJson = fcRef.current.toJSON(CUSTOM_PROPS);
            // Post-process: mark background objects, textTransform, and guide lines
            const canvasObjects = fcRef.current.getObjects();
            currentJson.objects?.forEach((objJson, idx) => {
                const liveObj = canvasObjects[idx];
                if (!liveObj) return;
                if (liveObj.isBackground) objJson.isBackground = true;
                if (liveObj.textTransform) objJson.textTransform = liveObj.textTransform;
                if (liveObj.isGuide) {
                    objJson.isGuide = true;
                    objJson.guideOrientation = liveObj.guideOrientation;
                }
                if (liveObj.isPlaceholder) objJson.isPlaceholder = true;
                if (liveObj.placeholderKey) objJson.placeholderKey = liveObj.placeholderKey;
            });
            const allPages = [...pages];
            allPages[currentPage] = currentJson;

            // Collect placeholders from all pages
            const used = new Set();
            allPages.forEach(p => {
                (p?.objects || []).forEach(o => {
                    if (o.isPlaceholder && o.placeholderKey) used.add(o.placeholderKey);
                    if (o.text) PLACEHOLDERS.forEach(ph => { if (o.text.includes(ph.key)) used.add(ph.key); });
                });
            });

            const contentObj = { pages: allPages, format };
            const orientation = format.includes('landscape') ? 'landscape' : 'portrait';

            await templateApi.update(template.id, {
                name: template.name, type: template.type,
                content: JSON.stringify(contentObj), is_active: template.is_active,
                page_width: fmt.w, page_height: fmt.h, orientation, placeholders: [...used],
            });
            setPages(allPages);
            toast.success('Vorlage gespeichert');
        } catch (err) {
            toast.error('Fehler beim Speichern');
        } finally { setSaving(false); }
    };

    // === PDF Preview ===
    const handlePreview = async () => {
        // Save first, then generate preview
        await handleSave();
        setPreviewing(true);
        try {
            const res = await templateApi.previewPdf(template.id);
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
        } catch { toast.error('Fehler bei PDF-Vorschau'); }
        finally { setPreviewing(false); }
    };

    // === Export current template ===
    const handleExport = async () => {
        await handleSave();
        try {
            const res = await templateApi.exportTemplate(template.id);
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url; a.download = `${template.name}.bgpr-template.json`; a.click();
            URL.revokeObjectURL(url);
            toast.success('Vorlage exportiert');
        } catch { toast.error('Fehler beim Exportieren'); }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const h = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if ((e.key === 'Delete' || e.key === 'Backspace') && selObj?.type !== 'textbox') deleteObj();
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 's') { e.preventDefault(); handleSave(); }
            }
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    });

    if (loading) {
        return <AdminLayout><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div></AdminLayout>;
    }

    const s = zoom / 100;

    return (
        <AdminLayout>
            <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="template-editor">
                {/* Toolbar */}
                <div className="flex items-center justify-between px-4 py-2 bg-card border-b shrink-0">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings/templates')} data-testid="back-to-templates">
                            <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
                        </Button>
                        <span className="text-sm text-muted-foreground">|</span>
                        <span className="text-sm font-semibold" data-testid="template-name">{template?.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {/* Format selector */}
                        <Select value={format} onValueChange={changeFormat}>
                            <SelectTrigger className="h-8 w-44 text-xs" data-testid="format-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(FORMATS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span className="text-sm text-muted-foreground mx-1">|</span>
                        <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(25, z-10))} data-testid="zoom-out-btn"><ZoomOut className="h-4 w-4" /></Button>
                        <span className="text-xs text-muted-foreground w-10 text-center">{zoom}%</span>
                        <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(200, z+10))} data-testid="zoom-in-btn"><ZoomIn className="h-4 w-4" /></Button>
                        <span className="text-sm text-muted-foreground mx-1">|</span>
                        <Button variant="outline" size="sm" onClick={openVersions} title="Versionsverlauf der Vorlage" data-testid="versions-btn">
                            <History className="h-4 w-4 mr-1" /> Verlauf
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleExport} title="Vorlage exportieren" data-testid="export-btn">
                            <Download className="h-4 w-4 mr-1" /> Export
                        </Button>
                        <Button variant="outline" size="sm" onClick={handlePreview} disabled={previewing} title="PDF-Vorschau" data-testid="preview-btn">
                            {previewing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                            Vorschau
                        </Button>
                        <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-template-btn">
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                            Speichern
                        </Button>
                    </div>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Left Sidebar */}
                    <div className="w-56 bg-card border-r overflow-y-auto shrink-0 p-3 space-y-4">
                        {/* Page tabs */}
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Seiten</p>
                            <div className="space-y-1">
                                {pages.map((_, idx) => (
                                    <div key={idx} className={`flex items-center gap-1 rounded px-2 py-1.5 text-xs cursor-pointer transition
                                        ${idx === currentPage ? 'bg-primary/10 text-primary font-semibold border border-primary/30' : 'hover:bg-muted'}`}>
                                        <button className="flex-1 text-left" onClick={() => switchPage(idx)} data-testid={`page-tab-${idx}`}>
                                            Seite {idx + 1}
                                        </button>
                                        {pages.length > 1 && (
                                            <button onClick={(e) => { e.stopPropagation(); deletePage(idx); }}
                                                className="text-muted-foreground hover:text-red-600 p-0.5" title="Seite löschen"
                                                data-testid={`delete-page-${idx}`}>
                                                <X className="h-3 w-3" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <Button variant="outline" size="sm" className="w-full text-xs mt-1" onClick={addPage} data-testid="add-page-btn">
                                    <Plus className="h-3 w-3 mr-1" /> Seite hinzufügen
                                </Button>
                            </div>
                        </div>

                        {/* Elements */}
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Elemente</p>
                            <div className="grid grid-cols-2 gap-1.5">
                                <Button variant="outline" size="sm" className="h-14 flex-col gap-1 text-xs" onClick={() => addText()} disabled={!ready} data-testid="add-text-btn"><Type className="h-4 w-4" /> Text</Button>
                                <Button variant="outline" size="sm" className="h-14 flex-col gap-1 text-xs" onClick={() => fileInputRef.current?.click()} disabled={!ready} data-testid="add-image-btn"><ImageIcon className="h-4 w-4" /> Bild</Button>
                                <Button variant="outline" size="sm" className="h-14 flex-col gap-1 text-xs" onClick={addRectangle} disabled={!ready} data-testid="add-rect-btn"><Square className="h-4 w-4" /> Rechteck</Button>
                                <Button variant="outline" size="sm" className="h-14 flex-col gap-1 text-xs" onClick={addHLine} disabled={!ready} data-testid="add-line-btn"><Minus className="h-4 w-4" /> Linie</Button>
                                <Button variant="outline" size="sm" className="h-14 flex-col gap-1 text-xs col-span-2" onClick={addQrPlaceholder} disabled={!ready} data-testid="add-qr-btn"><QrCode className="h-4 w-4" /> QR-Code</Button>
                            </div>
                            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                        </div>

                        {/* Background */}
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Hintergrund</p>
                            {getBackgroundObj() ? (
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2 px-2 py-1.5 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                                        <ImageIcon className="h-3 w-3 shrink-0" />
                                        <span>Hintergrundbild gesetzt</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        <Button variant="outline" size="sm" className="text-xs" onClick={() => bgInputRef.current?.click()} disabled={!ready} data-testid="change-bg-btn">
                                            <ImageIcon className="h-3 w-3 mr-1" /> Tauschen
                                        </Button>
                                        <Button variant="outline" size="sm" className="text-xs text-red-600" onClick={removeBackground} disabled={!ready} data-testid="remove-bg-btn">
                                            <Trash2 className="h-3 w-3 mr-1" /> Entfernen
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Button variant="outline" size="sm" className="w-full text-xs h-10" onClick={() => bgInputRef.current?.click()} disabled={!ready} data-testid="set-bg-btn">
                                    <ImageIcon className="h-4 w-4 mr-1.5" /> Hintergrundbild setzen
                                </Button>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-1.5">PNG oder JPG. Wird seitengroß skaliert und fixiert.</p>
                            <input ref={bgInputRef} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleBackgroundUpload} />
                        </div>

                        {/* Guides */}
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Hilfslinien</p>
                            <div className="grid grid-cols-2 gap-1.5">
                                <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => addHGuide()} disabled={!ready} data-testid="add-h-guide-btn">
                                    <Minus className="h-3 w-3 mr-1 text-cyan-500" /> Horizontal
                                </Button>
                                <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => addVGuide()} disabled={!ready} data-testid="add-v-guide-btn">
                                    <span className="inline-block w-3 h-3 mr-1 text-cyan-500 rotate-90"><Minus className="h-3 w-3" /></span> Vertikal
                                </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                                <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => addHGuide(canvasH / 2)} disabled={!ready} data-testid="add-h-center-btn">
                                    Mitte ―
                                </Button>
                                <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => addVGuide(canvasW / 2)} disabled={!ready} data-testid="add-v-center-btn">
                                    Mitte |
                                </Button>
                            </div>
                            {ready && getGuideCount() > 0 && (
                                <Button variant="ghost" size="sm" className="text-[10px] text-red-500 w-full mt-1" onClick={clearAllGuides} data-testid="clear-guides-btn">
                                    <Trash2 className="h-3 w-3 mr-1" /> Alle Hilfslinien entfernen ({getGuideCount()})
                                </Button>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-1.5">Hilfslinien werden nicht im PDF gedruckt.</p>
                        </div>

                        {/* Placeholders */}
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Platzhalter</p>
                            <div className="space-y-1">
                                {PLACEHOLDERS.map(p => (
                                    <button key={p.key} onClick={() => addPlaceholder(p)} disabled={!ready}
                                        className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-muted transition flex items-center gap-2 disabled:opacity-50"
                                        data-testid={`placeholder-${p.key}`}>
                                        <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                                        <span className="font-medium">{p.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Object Actions */}
                        {selObj && (
                            <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Aktionen</p>
                                <div className="grid grid-cols-2 gap-1.5">
                                    <Button variant="outline" size="sm" className="text-xs" onClick={duplicateObj} data-testid="duplicate-btn"><Copy className="h-3 w-3 mr-1" /> Kopie</Button>
                                    <Button variant="outline" size="sm" className="text-xs text-red-600" onClick={deleteObj} data-testid="delete-obj-btn"><Trash2 className="h-3 w-3 mr-1" /> Löschen</Button>
                                </div>
                                <div className="grid grid-cols-4 gap-1 mt-1.5">
                                    <Button variant="ghost" size="sm" onClick={toFront} title="Ganz vorne"><ChevronsUp className="h-3 w-3" /></Button>
                                    <Button variant="ghost" size="sm" onClick={bringFwd} title="Eins vor"><ChevronUp className="h-3 w-3" /></Button>
                                    <Button variant="ghost" size="sm" onClick={sendBwd} title="Eins zurück"><ChevronDown className="h-3 w-3" /></Button>
                                    <Button variant="ghost" size="sm" onClick={toBack} title="Ganz hinten"><ChevronsDown className="h-3 w-3" /></Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Canvas Area */}
                    <div className="flex-1 overflow-auto bg-muted/50 flex items-start justify-center p-8">
                        <div style={{ transform: `scale(${s})`, transformOrigin: 'top center', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
                            <canvas ref={canvasElRef} data-testid="fabric-canvas" />
                        </div>
                    </div>

                    {/* Right Sidebar - Properties */}
                    <div className="w-64 bg-card border-l overflow-y-auto shrink-0 p-3">
                        {selObj ? (
                            <div className="space-y-4">
                                <p className="text-xs font-semibold text-muted-foreground uppercase">
                                    {props.isGuide ? (props.guideOrientation === 'horizontal' ? 'Hilfslinie (horizontal)' : 'Hilfslinie (vertikal)') : props.isPlaceholder ? 'Platzhalter' : props.type === 'textbox' ? 'Text' : props.type === 'rect' ? 'Rechteck' : props.type === 'line' ? 'Linie' : props.type === 'image' ? 'Bild' : 'Element'}
                                </p>

                                {props.isGuide ? (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 px-2 py-1.5 bg-cyan-50 border border-cyan-200 rounded text-xs text-cyan-700">
                                            <Minus className="h-3 w-3 shrink-0" />
                                            <span>{props.guideOrientation === 'horizontal' ? 'Horizontale' : 'Vertikale'} Hilfslinie</span>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Position ({props.guideOrientation === 'horizontal' ? 'Y' : 'X'}) in mm
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.5"
                                                value={props.guideOrientation === 'horizontal' ? props.guidePosMMY : props.guidePosMMX}
                                                className="h-7 text-xs"
                                                data-testid="guide-pos-mm"
                                                onChange={e => setGuidePosMM(parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">Position in px</Label>
                                            <Input
                                                type="number"
                                                value={props.guideOrientation === 'horizontal' ? props.top : props.left}
                                                className="h-7 text-xs"
                                                data-testid="guide-pos-px"
                                                onChange={e => {
                                                    const o = fcRef.current?.getActiveObject();
                                                    if (!o) return;
                                                    if (props.guideOrientation === 'horizontal') o.set('top', +e.target.value);
                                                    else o.set('left', +e.target.value);
                                                    o.setCoords();
                                                    fcRef.current.renderAll();
                                                    setProps(readProps(o));
                                                }}
                                            />
                                        </div>
                                        <Button variant="outline" size="sm" className="w-full text-xs text-red-600" onClick={deleteObj} data-testid="delete-guide-btn">
                                            <Trash2 className="h-3 w-3 mr-1" /> Hilfslinie entfernen
                                        </Button>
                                        <p className="text-[10px] text-muted-foreground">Hilfslinien werden nicht im PDF gedruckt. Nur zur Ausrichtung.</p>
                                    </div>
                                ) : (
                                <>
                                {props.isPlaceholder && <div className="px-2 py-1 bg-blue-50 rounded text-xs text-blue-700" data-testid="placeholder-info">{props.placeholderKey}</div>}

                                <div>
                                    <p className="text-xs font-medium mb-1.5">Position & Größe</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label className="text-xs text-muted-foreground">X</Label><Input type="number" step={1} value={props.left} className="h-7 text-xs" onChange={e => setProp('left', +e.target.value)} /></div>
                                        <div><Label className="text-xs text-muted-foreground">Y</Label><Input type="number" step={1} value={props.top} className="h-7 text-xs" onChange={e => setProp('top', +e.target.value)} /></div>
                                        <div><Label className="text-xs text-muted-foreground">B</Label><Input type="number" step={1} value={props.width} className="h-7 text-xs" onChange={e => setProp('width', +e.target.value)} /></div>
                                        <div><Label className="text-xs text-muted-foreground">H</Label><Input type="number" step={1} value={props.height} className="h-7 text-xs" onChange={e => setProp('height', +e.target.value)} /></div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><Label className="text-xs text-muted-foreground">Drehung</Label><Input type="number" value={props.angle} className="h-7 text-xs" onChange={e => setProp('angle', +e.target.value)} /></div>
                                    <div><Label className="text-xs text-muted-foreground">Deckkraft %</Label><Input type="number" value={props.opacity} min={0} max={100} className="h-7 text-xs" onChange={e => setProp('opacity', +e.target.value)} /></div>
                                </div>

                                {(props.type === 'textbox' || props.type === 'i-text' || props.type === 'text') && (
                                    <>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">Schriftart</Label>
                                            <Select value={props.fontFamily} onValueChange={v => setProp('fontFamily', v)}>
                                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {editorFonts.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div><Label className="text-xs text-muted-foreground">Größe</Label><Input type="number" value={props.fontSize} min={6} max={120} className="h-7 text-xs" onChange={e => setProp('fontSize', e.target.value)} /></div>
                                            <div><Label className="text-xs text-muted-foreground">Zeilenabstand</Label><Input type="number" value={props.lineHeight} min={0.5} max={4} step={0.05} className="h-7 text-xs" data-testid="line-height-input" onChange={e => setProp('lineHeight', e.target.value)} /></div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div><Label className="text-xs text-muted-foreground">Farbe</Label>
                                            <div className="flex gap-1">
                                                <input type="color" value={props.fill} className="w-7 h-7 rounded cursor-pointer border p-0.5" onChange={e => setProp('fill', e.target.value)} />
                                                <Input value={props.fill} className="h-7 text-xs flex-1 font-mono" onChange={e => setProp('fill', e.target.value)} />
                                            </div></div>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button variant={props.fontWeight==='bold'?'default':'outline'} size="sm" className="h-7 w-7 p-0" onClick={()=>setProp('fontWeight',props.fontWeight==='bold'?'normal':'bold')}><Bold className="h-3 w-3"/></Button>
                                            <Button variant={props.fontStyle==='italic'?'default':'outline'} size="sm" className="h-7 w-7 p-0" onClick={()=>setProp('fontStyle',props.fontStyle==='italic'?'normal':'italic')}><Italic className="h-3 w-3"/></Button>
                                            <Button variant={props.underline?'default':'outline'} size="sm" className="h-7 w-7 p-0" onClick={()=>setProp('underline',!props.underline)}><Underline className="h-3 w-3"/></Button>
                                            <Button
                                                variant={props.textTransform==='uppercase'?'default':'outline'} size="sm" className="h-7 w-7 p-0"
                                                title="Großbuchstaben"
                                                data-testid="text-uppercase-btn"
                                                onClick={() => {
                                                    const o = fcRef.current?.getActiveObject();
                                                    if (!o) return;
                                                    const newVal = props.textTransform === 'uppercase' ? 'none' : 'uppercase';
                                                    o.textTransform = newVal;
                                                    fcRef.current.renderAll();
                                                    setProps(readProps(o));
                                                }}
                                            ><CaseUpper className="h-3 w-3"/></Button>
                                            <span className="w-px bg-border mx-1"/>
                                            <Button variant={props.textAlign==='left'?'default':'outline'} size="sm" className="h-7 w-7 p-0" onClick={()=>setProp('textAlign','left')}><AlignLeft className="h-3 w-3"/></Button>
                                            <Button variant={props.textAlign==='center'?'default':'outline'} size="sm" className="h-7 w-7 p-0" onClick={()=>setProp('textAlign','center')}><AlignCenter className="h-3 w-3"/></Button>
                                            <Button variant={props.textAlign==='right'?'default':'outline'} size="sm" className="h-7 w-7 p-0" onClick={()=>setProp('textAlign','right')}><AlignRight className="h-3 w-3"/></Button>
                                        </div>
                                    </>
                                )}

                                {(props.type==='rect'||props.type==='circle'||props.type==='line') && !props.isGuide && (
                                    <>
                                        <div><Label className="text-xs text-muted-foreground">Füllfarbe</Label>
                                        <div className="flex gap-1">
                                            <input type="color" value={props.fill||'#ffffff'} className="w-7 h-7 rounded cursor-pointer border p-0.5" onChange={e=>setProp('fill',e.target.value)} />
                                            <Input value={props.fill||''} placeholder="transparent" className="h-7 text-xs flex-1 font-mono" onChange={e=>setProp('fill',e.target.value)} />
                                        </div></div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div><Label className="text-xs text-muted-foreground">Rahmenfarbe</Label><input type="color" value={props.stroke||'#000000'} className="w-full h-7 rounded cursor-pointer border p-0.5" onChange={e=>setProp('stroke',e.target.value)} /></div>
                                            <div><Label className="text-xs text-muted-foreground">Rahmenstärke</Label><Input type="number" value={props.strokeWidth} min={0} max={20} className="h-7 text-xs" onChange={e=>setProp('strokeWidth',+e.target.value)} /></div>
                                        </div>
                                    </>
                                )}
                                </>
                                )}
                            </div>
                        ) : (
                            <div className="text-center text-muted-foreground text-sm py-12">
                                <p className="font-medium mb-1">Kein Element ausgewählt</p>
                                <p className="text-xs">Klicken Sie auf ein Element oder fügen Sie ein neues hinzu.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Versions-Verlauf-Dialog */}
            <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
                <DialogContent className="max-w-2xl" data-testid="versions-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="h-5 w-5" /> Versionsverlauf
                        </DialogTitle>
                        <DialogDescription>
                            Bei jeder Speicherung wird der vorherige Stand automatisch gesichert.
                            Die letzten 10 Versionen sind verfügbar — neueste zuerst.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto -mx-2 px-2">
                        {versionsLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : versions.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-6 text-center">
                                Noch keine Versionen vorhanden. Eine Version entsteht beim nächsten Speichern.
                            </p>
                        ) : (
                            versions.map((v, idx) => {
                                const date = new Date(v.saved_at);
                                const dateStr = date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
                                const isEmpty = (v.object_count || 0) === 0;
                                return (
                                    <div key={v.id} className={`flex items-center justify-between gap-3 p-3 rounded border ${isEmpty ? 'bg-amber-50/40 border-amber-200' : 'bg-card hover:bg-muted/40'}`} data-testid={`version-row-${idx}`}>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">{dateStr}</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {v.saved_by || 'unbekannt'}
                                                {' · '}
                                                {isEmpty
                                                    ? <span className="text-amber-700 font-medium">Leerer Stand</span>
                                                    : <>{v.object_count} Element{v.object_count === 1 ? '' : 'e'}</>
                                                }
                                            </p>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => restoreVersion(v.id, v.object_count || 0)}
                                            data-testid={`restore-version-${idx}`}
                                        >
                                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                            Wiederherstellen
                                        </Button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVersionsOpen(false)} data-testid="versions-close">Schließen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
